import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "@/services/authService";
import { User, Group, Permission } from "@/types/auth";

interface UserContextType {
  user: User | null;
  groups: Group[];
  permissions: Permission[];
  isLoading: boolean;
  error: string | null;
  hasPermission: (ruta: string, menu: string, funcion: string) => boolean;
  refreshUser: () => Promise<void>;
  cognitoUser: any; // Cognito user object
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
  cognitoUser?: any;
}

/**
 * Proveedor de contexto de usuario con sincronización automática
 * Conectado al backend NestJS + PostgreSQL según PLANIFICACION-SISTEMA.md
 *
 * Flujo de sincronización:
 * 1. Usuario se autentica con Cognito via Amplify UI
 * 2. Frontend obtiene token JWT de Cognito
 * 3. Solicitud automática a /api/auth/sync-user en NestJS
 * 4. Backend valida token JWT con AWS SDK v3
 * 5. Busca/crea usuario en PostgreSQL
 * 6. Respuesta con usuario + grupos + permisos
 * 7. Frontend almacena datos en contexto
 */
export function UserProvider({
  children,
  cognitoUser,
}: UserProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Sincronización automática con backend al cambiar usuario de Cognito
   */
  useEffect(() => {
    const syncUserWithBackend = async () => {
      if (!cognitoUser) {
        setUser(null);
        setGroups([]);
        setPermissions([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Llamada al endpoint de sincronización según planificación
        const response = await authService.syncUser();

        setUser(response.data.user);
        setGroups(response.data.groups || []);
        setPermissions(response.data.permissions || []);
      } catch (err) {
        console.error("Error sincronizando usuario:", err);
        setError("Error al cargar datos del usuario");

        // Fallback con datos básicos de Cognito
        setUser({
          id: (cognitoUser as any)?.userId || "unknown",
          email:
            (cognitoUser as any)?.signInDetails?.loginId ||
            "unknown@example.com",
          nombre: (cognitoUser as any)?.attributes?.given_name || "Usuario",
          apellido: (cognitoUser as any)?.attributes?.family_name || "",
          activo: true,
          fecha_creacion: new Date().toISOString(),
          cognito_user_id: (cognitoUser as any)?.userId || "unknown",
        });
      } finally {
        setIsLoading(false);
      }
    };

    syncUserWithBackend();
  }, [cognitoUser]);

  /**
   * Función para verificar permisos según estructura JSON jerárquica
   * Soporta múltiples formatos:
   * - Formato oficial: rutas → menús → funciones → boolean
   * - Formato antiguo: [ruta][menu][funcion] → boolean
   * - Formato global: all.all.all = true
   * Ejemplo: hasPermission("/usuarios", "gestion-usuarios", "crear")
   */
  const hasPermission = (
    ruta: string,
    menu: string,
    funcion: string,
  ): boolean => {
    if (!user || !permissions.length) {
      return false; // Sin usuario o permisos = sin acceso
    }

    // Normalizar ruta
    const normalizedRuta = ruta.startsWith("/") ? ruta : `/${ruta}`;

    // Buscar permiso 'all' que otorga acceso total
    const allPermission = permissions.find((p) => p.nombre === "all");
    if (allPermission) {
      return true;
    }

    // Buscar en estructura de permisos
    for (const permission of permissions) {
      const estructura = permission.estructura_permisos as any;

      // 1. Verificar permiso global all.all.all
      if (estructura?.["all"]?.["all"]?.["all"] === true) {
        return true;
      }

      // 2. Verificar formato oficial: rutas.menus.funciones
      const rutas = estructura?.["rutas"] || estructura?.["RUTAS"];
      if (rutas) {
        const rutaData = rutas[normalizedRuta] || rutas[ruta];
        if (rutaData) {
          const menus = rutaData?.["menus"] || rutaData?.["MENUS"];
          if (menus) {
            const menuData = menus[menu];
            if (menuData) {
              const funciones =
                menuData?.["funciones"] || menuData?.["FUNCIONES"];
              if (funciones && funciones[funcion] === true) {
                return true;
              }
            }
          }
        }
      }

      // 3. Verificar formato antiguo: [ruta][menu][funcion]
      if (estructura[normalizedRuta]?.[menu]?.[funcion] === true) {
        return true;
      }
      if (estructura[ruta]?.[menu]?.[funcion] === true) {
        return true;
      }
    }

    return false;
  };

  /**
   * Función para refrescar datos de usuario desde backend
   */
  const refreshUser = async (): Promise<void> => {
    if (!cognitoUser) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.syncUser();
      setUser(response.data.user);
      setGroups(response.data.groups || []);
      setPermissions(response.data.permissions || []);
    } catch (err) {
      console.error("Error refrescando usuario:", err);
      setError("Error al actualizar datos del usuario");
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: UserContextType = {
    user,
    groups,
    permissions,
    isLoading,
    error,
    hasPermission,
    refreshUser,
    cognitoUser,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

/**
 * Hook para usar el contexto de usuario
 */
export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser debe ser usado dentro de UserProvider");
  }
  return context;
}
